
<script>
import { mapGetters } from 'vuex';

export default {
	name: 'ConditionalRenderingMixin',
	computed: {
		...mapGetters(['getAudience', 'getFundAttributes']),
		conditionalRenderingAudience() {
			return this.getAudience || '';
		},
		conditionalRenderingObjective() {
			return this.getFundAttributes?.Objective.toLowerCase() || '';
		},
		conditionalRenderingClassSymbol() {
			return this.getFundAttributes?.ClassSymbol.toLowerCase() || '';
		},
		conditionalRenderingFundSymbol() {
			return this.getFundAttributes?.FundSymbol.toLowerCase() || '';
		},
		isAuthorizedModule() {
			const equityBreakdown = ![
				'assetallocation',
				'balanced',
				'equityincome',
				'dividendincomeappreciation',
				'bond',
				'capitalpreservation',
				'incomepreservation',
				'tax-exempt',
				'totalreturnfixedincome'
			].includes(this.conditionalRenderingObjective);
			const equityBondBreakdown = [
				'assetallocation',
				'balanced',
				'equityincome',
				'dividendincomeappreciation'
			].includes(this.conditionalRenderingObjective);
			const authorizedModules = {
				acquiredFees:
					['advisor', 'institutional', 'individual'].includes(
						this.conditionalRenderingAudience
					) &&
					['portfolioseries', 'managedrisk'].includes(
						this.conditionalRenderingObjective
					),
				bondBreakdown:
					['advisor', 'institutional', 'individual'].includes(
						this.conditionalRenderingAudience
					) &&
					[
						'bond',
						'capitalpreservation',
						'incomepreservation',
						'tax-exempt',
						'totalreturnfixedincome'
					].includes(this.conditionalRenderingObjective),
				breakdownEquityTopEquity: ![ //This condition is inside another conditional module, so both need to be true
					'managedrisk',
					'portfolioseries',
					'retirementtargetdate'
				].includes(this.conditionalRenderingObjective)
				&& equityBreakdown,
				//this conditional is a child of the "equityBondBreakdown" conditional. Its conditions are the same (although you do need to click on the bond tab to possibly see it)
				detailsBreakdownEquityAndBondQuarterlyHoldingsLink: equityBondBreakdown,
				equityBondBreakdown,
				equityBreakdown,
				fundStatistics: this.conditionalRenderingObjective.includes(
					'targetdate'
				),
				fundStatistics_position2: ['bond'].includes(
					this.conditionalRenderingObjective
				),
				fundStatistics_position3:
					[
						'assetallocation',
						'balanced',
						'capitalpreservation',
						'equityincome',
						'incomepreservation',
						'totalreturnfixedincome'
					].includes(this.conditionalRenderingObjective) ||
					(
						this.conditionalRenderingObjective === 'dividendincomeappreciation'
						&& this.conditionalRenderingFundSymbol === 'vicib'
					),
				geoBreakdown:
					![
						'vismid-1',
						'vismid-1a',
						'vismid-2',
						'vismid-4'
					].includes(this.conditionalRenderingClassSymbol)
					&& ['advisor', 'institutional', 'individual'].includes(
						this.conditionalRenderingAudience
					) &&
					([
						'growth',
						'assetallocation',
						'equityincome',
						'balanced',
						'portfolioseries',
						'retirementtargetdate',
						'capitalappreciation',
						'dividendincomeappreciation'
					].includes(this.conditionalRenderingObjective) ||
						[
							'ciggi-1',
							'ciggi-1a',
							'ciggi-2',
							'ciggi-4',
							'vigi-1',
							'vigi-1a',
							'vigi-2',
							'vigi-4',
							'viigi-1',
							'viigi-1a',
							'viigi-2',
							'viigi-4',
							'vigbf-1',
							'vigbf-1a',
							'vigbf-2',
							'vigbf-4',
							'vihib-1',
							'vihib-1a',
							'vihib-2',
							'vihib-4',
							'vibf-1',
							'vibf-1a',
							'vibf-2',
							'vibf-4',
							'vimaa-p1',
							'vimaa-p2',
							'vimg-p1',
							'vimg-p2',
							'vimgip-P1',
							'vimgi-p2',
							'vimi-p1',
							'vimi-p2'
						].includes(this.conditionalRenderingClassSymbol)),
				geoInvesting:
					![
						'vismid-1',
						'vismid-1a',
						'vismid-2',
						'vismid-4'
					].includes(this.conditionalRenderingClassSymbol)
					&& ['advisor', 'institutional', 'individual'].includes(
						this.conditionalRenderingAudience
					) &&
					[
						'growth',
						'growthincome',
						'assetallocation',
						'equityincome',
						'balanced',
						'capitalappreciation',
						'dividendincomeappreciation'
					].includes(this.conditionalRenderingObjective),
				growth10k:
					!this.conditionalRenderingObjective.includes(
						'targetdate'
					) && this.conditionalRenderingAudience === 'individual',
				mStarOwnership: [
					'growth',
					'growthincome',
					'equity',
					'equityincome',
					'balanced',
					'capitalappreciation',
					'dividendincomeappreciation',
					'assetallocation'
				].includes(this.conditionalRenderingObjective),
				mStarStyle:
					['bond'].includes(this.conditionalRenderingObjective) &&
					['vicib-1', 'vicib-1a', 'vicib-2', 'vicib-4'].includes(
						this.conditionalRenderingClassSymbol
					) &&
					this.conditionalRenderingFundSymbol !== 'viubf',
				mStarStyle_position2:
					this.conditionalRenderingObjective != 'bond' &&
					([
						'balanced',
						'equityincome',
						'incomepreservation',
						'totalreturnfixedincome',
						'capitalpreservation',
						'assetallocation'
					].includes(this.conditionalRenderingObjective) ||
						['vicib-1', 'vicib-1a', 'vicib-2', 'vicib-4'].includes(
							this.conditionalRenderingClassSymbol
						)) &&
						this.conditionalRenderingFundSymbol !== 'viubf',
				marketCap: [
					'growth',
					'growthincome',
					'equity',
					'equityincome',
					'balanced',
					'capitalappreciation',
					'dividendincomeappreciation',
					'assetallocation'
				].includes(this.conditionalRenderingObjective),
				portfolioManagementLearnLink:
					this.conditionalRenderingAudience !== 'individual' ||
					this.conditionalRenderingObjective ===
						'retirementtargetdate',
				returnsAtNav:
					!['portfolioseries'].includes(
						this.conditionalRenderingObjective
					) &&
					!this.conditionalRenderingObjective.includes('targetdate'),
				//56
				returnsAtNav_position2: ['portfolioseries'].includes(
					this.conditionalRenderingObjective
				),
				returnsAtNav_position3: this.conditionalRenderingObjective.includes(
					'targetdate'
				),
				riskRatingAverageLife:
					[
						'assetallocation',
						'balanced',
						'bond',
						'managedrisk',
						'capitalpreservation',
						'incomepreservation',
						'equityincome',
						'portfolioseries',
						'retirementtargetdate',
						'tax-exempt',
						'totalreturnfixedincome'
					].includes(this.conditionalRenderingObjective) ||
					['vicib-1', 'vicib-1a', 'vicib-2', 'vicib-4'].includes(
						this.conditionalRenderingClassSymbol
					),
				riskRatingQualitySummary:
					[
						'assetallocation',
						'balanced',
						'bond',
						'managedrisk',
						'capitalpreservation',
						'incomepreservation',
						'equityincome',
						'portfolioseries',
						'retirementtargetdate',
						'tax-exempt',
						'totalreturnfixedincome'
						//75
					].includes(this.conditionalRenderingObjective) ||
					['vicib-1', 'vicib-1a', 'vicib-2', 'vicib-4'].includes(
						this.conditionalRenderingClassSymbol
					),
				underlyingFunds:
					['portfolioseries'].includes(
						this.conditionalRenderingObjective
					) ||
					this.conditionalRenderingObjective.includes(
						'managedrisk'
					) ||
					this.conditionalRenderingObjective.includes('targetdate'),
				valuation: [
					'assetallocation',
					'balanced',
					'equityincome',
					'capitalappreciation',
					'growth',
					'growthincome',
					'portfolioseries',
					'retirementtargetdate'
				].includes(this.conditionalRenderingObjective),
				//55
				volatilityReturns: this.conditionalRenderingObjective.includes(
					'targetdate'
				),
				volatilityReturns_position2:
					!this.conditionalRenderingObjective.includes(
						'targetdate'
					) && this.conditionalRenderingAudience !== 'individual',

				returnsVolatility:
					this.conditionalRenderingObjective !==
						'retirementtargetdate' &&
					this.conditionalRenderingAudience === 'individual',
				returnsGrowth10K:
					this.conditionalRenderingObjective ==
						'retirementtargetdate' ||
					this.conditionalRenderingAudience !== 'individual'
			};
			return authorizedModules;
		}
	}
};
</script>
